/**
 * @fileoverview Reusable slide-over panel component
 *
 * A right-side slide-over panel that displays content without navigating
 * away from the current page. Includes backdrop, escape key handling,
 * and smooth animations.
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * SlideOverPanel - Right-side slide-over panel with backdrop
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is currently open
 * @param {Function} props.onClose - Callback when panel should close
 * @param {string} [props.width='65vw'] - Panel width (CSS value)
 * @param {string} [props.title] - Optional title to display in header
 * @param {Function} [props.onNavigatePrevious] - Callback for left arrow navigation
 * @param {Function} [props.onNavigateNext] - Callback for right arrow navigation
 * @param {{current: number, total: number}} [props.position] - Current position in list
 * @param {React.ReactNode} props.children - Panel content
 */
function SlideOverPanel({
  isOpen,
  onClose,
  width = '65vw',
  title = '',
  onNavigatePrevious,
  onNavigateNext,
  position,
  children,
}) {
  // Animation state: controls whether panel is visible and animated in
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  // Ref for scrollable content area
  const contentRef = useRef(null);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      // Opening: render first, then trigger animation
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      // Closing: animate out first, then unmount
      setIsAnimatingIn(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      // Check if user is editing text (input, textarea, or contenteditable)
      const activeEl = document.activeElement;
      const isEditing =
        activeEl?.tagName === 'INPUT' ||
        activeEl?.tagName === 'TEXTAREA' ||
        activeEl?.isContentEditable;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && !isEditing && onNavigatePrevious) {
        e.preventDefault();
        onNavigatePrevious();
      } else if (e.key === 'ArrowRight' && !isEditing && onNavigateNext) {
        e.preventDefault();
        onNavigateNext();
      } else if (e.key === 'ArrowUp' && !isEditing && contentRef.current) {
        e.preventDefault();
        const scrollAmount = contentRef.current.clientHeight / 2;
        contentRef.current.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
      } else if (e.key === 'ArrowDown' && !isEditing && contentRef.current) {
        e.preventDefault();
        const scrollAmount = contentRef.current.clientHeight / 2;
        contentRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      }
    },
    [onClose, onNavigatePrevious, onNavigateNext]
  );

  // Setup escape key listener and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e) => {
      // Only close if clicking the backdrop itself, not panel content
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!shouldRender) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
          isAnimatingIn ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel container - handles click-outside detection */}
      <div
        className="absolute inset-0 flex justify-end"
        onClick={handleBackdropClick}
      >
        {/* Panel */}
        <div
          className={`relative h-full bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${
            isAnimatingIn ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ width, maxWidth: '90vw' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate pr-4">
                {title}
              </h2>
            )}
            <div className="ml-auto flex items-center gap-2">
              {/* Navigation controls */}
              {position && (
                <>
                  <button
                    onClick={onNavigatePrevious}
                    disabled={position.current <= 1}
                    className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400"
                    title="Previous item (←)"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums min-w-[4rem] text-center">
                    {position.current} of {position.total}
                  </span>
                  <button
                    onClick={onNavigateNext}
                    disabled={position.current >= position.total}
                    className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400"
                    title="Next item (→)"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Close (Esc)"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div ref={contentRef} className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SlideOverPanel;
